import React, { useState, useEffect, useCallback } from 'react';
import { fetchTaps } from '../../api/fetchTaps';
import { happyHourConfig } from '../../data/happyHourConfig';
import { siteConfig } from '../../data/siteConfig';
import { imageConfig } from '../../data/imageConfig';
import LoaderBeer from '../../components/LoaderBeer/LoaderBeer';
import './TVMode.css';

const TVMode = () => {
  const [taps, setTaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentTapIndex, setCurrentTapIndex] = useState(0);

  // Check if currently in happy hour
  const isHappyHour = () => {
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-AU', { weekday: 'long' }).toLowerCase();;
    const currentTime = now.toLocaleTimeString('en-AU', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });

    const isHappyHourDay = happyHourConfig.schedule.days.includes(currentDay);
    const isHappyHourTime = currentTime >= happyHourConfig.schedule.startTime &&
      currentTime <= happyHourConfig.schedule.endTime;

    return isHappyHourDay && isHappyHourTime;
  };

  // Load tap data
  const loadTaps = async () => {
    try {
      const response = await fetchTaps();
      if (response.success) {
        setTaps(response.data.filter(tap => tap.isAvailable));
      }
    } catch (err) {
      console.error('Error loading taps:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update current time
  const updateTime = () => {
    setCurrentTime(new Date());
  };

  // Cycle through taps
  const cycleTaps = useCallback(() => {
    setCurrentTapIndex((prev) => {
      if (taps.length === 0) return prev;
      return (prev + 1) % taps.length;
    });
  }, [taps.length]);

  // Filter available taps (must be defined before useEffect that uses it)
  const availableTaps = taps.filter(tap => tap.isAvailable);
  const currentTap = availableTaps.length > 0 ? availableTaps[currentTapIndex] : null;

  // Initial load
  useEffect(() => {
    loadTaps();
  }, []);

  // Auto refresh every 30 seconds
  useEffect(() => {
    const refreshInterval = setInterval(loadTaps, 30000);
    return () => clearInterval(refreshInterval);
  }, []);

  // Update time every second
  useEffect(() => {
    const timeInterval = setInterval(updateTime, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Cycle taps every 8 seconds
  useEffect(() => {
    if (availableTaps.length > 1) {
      const cycleInterval = setInterval(cycleTaps, 8000);
      return () => clearInterval(cycleInterval);
    }
  }, [availableTaps.length, cycleTaps]);

  if (loading) {
    return (
      <div className="tv-mode">
        <LoaderBeer size="large" message="Loading TV Display..." />
      </div>
    );
  }

  return (
    <div className="tv-mode">
      {/* Header */}
      <header className="tv-header">
        <div className="tv-brand">
          <img
            src={imageConfig.brand.logo}
            alt={imageConfig.brand.logoAlt}
            className="tv-logo"
          />
          <div className="tv-brand-text">
            <h1 className="tv-brand-name">{siteConfig.brand.name}</h1>
            <p className="tv-tagline">{siteConfig.brand.tagline}</p>
          </div>
        </div>

        <div className="tv-time">
          {currentTime.toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </div>
      </header>

      {/* Main Content */}
      <main className="tv-main">
        {/* Featured Tap */}
        {currentTap ? (
          <section className="tv-featured">
            <div className="featured-header">
              <h2>Featured on Tap {currentTap.tapNumber}</h2>
              {isHappyHour() && ['Hazy', 'Lager'].includes(currentTap.style) && (
                <div className="tv-happy-hour">
                  🍺 {happyHourConfig.offer.shortDescription}
                </div>
              )}
            </div>

            <div className="featured-content">
              <div className="featured-info">
                <h3 className="featured-beer">{currentTap.beerName}</h3>
                <p className="featured-brewery">{currentTap.breweryName}</p>

                <div className="featured-meta">
                  <span className="featured-style">{currentTap.style}</span>
                  <span className="featured-abv">{currentTap.abv}% ABV</span>
                </div>

                {currentTap.tastingNotes && (
                  <p className="featured-notes">{currentTap.tastingNotes}</p>
                )}

                <div className="featured-pricing">
                  <div className="price-item">
                    <span>Pot (285ml)</span>
                    <span>$8.50</span>
                  </div>
                  <div className="price-item">
                    <span>Schooner (425ml)</span>
                    <span>$13.50</span>
                  </div>
                  <div className="price-item">
                    <span>Pint (570ml)</span>
                    <span>$14.50</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="tv-featured">
            <div className="featured-info">
              <h3 className="featured-beer">No Taps Available</h3>
              <p className="featured-brewery">Please check back soon</p>
            </div>
          </section>
        )}

        {/* Tap Grid */}
        {availableTaps.length > 0 && (
          <section className="tv-grid">
            <h3 className="grid-title">All Available Taps</h3>
            <div className="taps-display">
              {availableTaps.map(tap => (
                <div
                  key={tap.tapNumber}
                  className={`tap-item ${tap.tapNumber === currentTap?.tapNumber ? 'tap-item--featured' : ''}`}
                >
                  <div className="tap-number">{tap.tapNumber}</div>
                  <div className="tap-details">
                    <h4 className="tap-beer">{tap.beerName}</h4>
                    <p className="tap-brewery">{tap.breweryName}</p>
                    <span className="tap-style">{tap.style}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer Ticker */}
      <footer className="tv-footer">
        <div className="ticker">
          <div className="ticker-content">
            <span className="ticker-item">
              🍺 {availableTaps.length} Beers on tap from Victorian breweries
            </span>
            <span className="ticker-item">
              🥘 BYO Food Welcome - Bring your favorite meal
            </span>
            <span className="ticker-item">
              👨‍👩‍👧‍👦 Family Friendly Environment
            </span>
            {isHappyHour() && (
              <span className="ticker-item ticker-item--happy">
                ⏰ {happyHourConfig.offer.title} NOW: {happyHourConfig.offer.shortDescription}
              </span>
            )}
            <span className="ticker-item">
              📍 {siteConfig.contact.address.full}
            </span>
            <span className="ticker-item">
              ⏰ Last updated: {currentTime.toLocaleTimeString('en-AU', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TVMode;